import { useMemo } from 'react';
import type { ColDef, ICellRendererParams, ValueGetterParams } from 'ag-grid-community';
import type { StarGridText } from 'src/components/star-grid/hooks/useStarGridText';
import { useAdminGridTheme } from 'src/components/star-grid/hooks/useSnapshotGrid';
import { formatUTCInUserTimeZone } from 'src/utilities/DateTimeUtilities';
import type { Dataset } from 'src/components/star-grid/datasets/datasetTypes';

type Getter = ValueGetterParams<Dataset>;

const isS3 = (ref: string): boolean => ref.startsWith('s3://');

export const useDatasetGrid = (text: StarGridText, actions: { readonly cellRenderer: string }) => {
  const theme = useAdminGridTheme();
  const columns = useMemo<ColDef<Dataset>[]>(
    () => [
      { colId: 'name', headerName: text('datasetsName'), minWidth: 220, flex: 2, valueGetter: ({ data }: Getter) => data?.name ?? '' },
      {
        colId: 'type',
        headerName: text('datasetsType'),
        width: 110,
        valueGetter: ({ data }: Getter) => (data ? text(isS3(data.ref) ? 'datasetsTypeS3' : 'datasetsTypeCatalog') : '')
      },
      {
        colId: 'ref',
        headerName: text('datasetsSource'),
        minWidth: 320,
        flex: 3,
        valueGetter: ({ data }: Getter) => data?.ref ?? '',
        tooltipValueGetter: ({ data }) => data?.ref ?? ''
      },
      {
        colId: 'status',
        headerName: text('datasetsStatus'),
        width: 120,
        valueGetter: ({ data }: Getter) => (data ? data.status.charAt(0).toUpperCase() + data.status.slice(1) : '')
      },
      {
        colId: 'version',
        headerName: text('datasetsVersion'),
        width: 100,
        type: 'numericColumn',
        valueGetter: ({ data }: Getter) => data?.version ?? null
      },
      { colId: 'owner', headerName: text('datasetsOwner'), width: 130, valueGetter: ({ data }: Getter) => data?.owner ?? data?.created_by ?? '' },
      {
        colId: 'created_at',
        headerName: text('datasetsCreated'),
        minWidth: 190,
        flex: 1,
        valueGetter: ({ data }: Getter) => (data?.created_at ? formatUTCInUserTimeZone(data.created_at) : '')
      },
      {
        colId: 'actions',
        headerName: '',
        width: 90,
        sortable: false,
        filter: false,
        resizable: false,
        suppressHeaderMenuButton: true,
        cellRenderer: actions.cellRenderer
      }
    ],
    [text, actions.cellRenderer]
  );
  return { theme, columns };
};

export type DatasetActionParams = ICellRendererParams<Dataset>;
